import React, { useState, useMemo } from "react";
import { DATA } from "./weeks/index"; // Import the data
import { 
  AppShell, PageHeader, Card, Pill, ProgressBar, 
  Icons, StandardModal, InfoModal, Button 
} from "./components/UI";

export default function App() {
  const [activeId, setActiveId] = useState(null);
  const [infoModalData, setInfoModalData] = useState(null);

  // Initialize checkbox state
  const [checkboxState, setCheckboxState] = useState(() => {
    const initial = {};
    DATA.forEach((week) => {
      week.deliverables.forEach((d) => {
        if ((d.links && d.links.length > 0) || d.link || d.defaultChecked) {
          initial[d.id] = true;
        }
      });
    });
    return initial;
  });

  const activeData = useMemo(() => DATA.find((d) => d.id === activeId), [activeId]);

  const toggleCheckbox = (id) => {
    setCheckboxState((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const getStats = (item) => {
    const benDeliverables = item.deliverables.filter(d => d.owner.includes("Ben"));
    const total = benDeliverables.length;
    const completed = benDeliverables.filter(d => checkboxState[d.id]).length;
    return { total, completed };
  };

  return (
    <AppShell>
      <PageHeader 
        title="Accountability Tracker" 
        description="Fairway Digital Media <> Coursera Engagement" 
        actions={
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <span className="text-label" style={{ color: "var(--success)" }}>On Track</span>
            <div style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "var(--success)" }}></div>
          </div>
        } 
      />

      <main className="container" style={{ paddingBottom: "var(--space-32)", paddingTop: "var(--space-32)" }}>
        <div style={{ marginBottom: "var(--space-24)" }}>
          <h3 className="text-heading-lg" style={{ marginBottom: "var(--space-8)" }}>Weekly Sprints</h3>
          <p className="text-body" style={{ color: "var(--ink-soft)", maxWidth: "600px" }}>
            Select a week to view detailed deliverables and update status.
          </p>
        </div>

        <div className="grid-responsive">
          {DATA.map((week) => {
            const stats = getStats(week);
            return (
              <Card key={week.id} onClick={() => setActiveId(week.id)} className="card-hover">
                <div style={{ marginBottom: "var(--space-16)" }}>
                  <div className="text-label" style={{ color: "var(--ink-lighter)", marginBottom: "var(--space-4)" }}>Week of {week.date}</div>
                  <h4 className="text-heading-lg" style={{ fontSize: "18px", margin: 0 }}>{week.title}</h4>
                </div>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "var(--space-16)" }}>{week.tags.map((t) => <Pill key={t} label={t} />)}</div>
                <div style={{ marginTop: "auto" }}><ProgressBar total={stats.total} completed={stats.completed} /></div>
                <div style={{ marginTop: "var(--space-16)", paddingTop: "var(--space-16)", borderTop: "1px solid var(--line)", display: "flex", justifyContent: "space-between", alignItems: "center", color: "var(--brand)", fontSize: "13px", fontWeight: 600 }}><span>View Deliverables</span><Icons.ArrowRight /></div>
              </Card>
            );
          })}
        </div>
      </main>

      {/* Main Detail Modal */}
      {activeId && activeData && (
        <StandardModal isOpen={!!activeId} onClose={() => setActiveId(null)} title={activeData.title}>
          {/* ... (Same Modal Logic as before, just using activeData) ... */}
           {/* Note: In a real multi-file refactor, you might want to extract this Modal content logic to a component too, but for now, pasting the previous JSX logic here works fine. */}
           <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "var(--space-32)" }}>
            <div>
              <div className="text-label" style={{ color: "var(--ink-lighter)", marginBottom: "var(--space-8)" }}>Summary</div>
              <p className="text-body" style={{ color: "var(--ink)" }}>{activeData.summary}</p>
              <div style={{ marginTop: "var(--space-24)" }}>
                <div className="text-label" style={{ color: "var(--ink-lighter)", marginBottom: "var(--space-8)" }}>Attendees</div>
                <div style={{ display: "flex", gap: "8px" }}><span style={{ background: "var(--bg)", padding: "4px 8px", borderRadius: "4px", fontSize: "14px", color: "var(--ink-soft)" }}>{activeData.attendees}</span></div>
              </div>
            </div>
            <div style={{ backgroundColor: "var(--bg)", padding: "var(--space-16)", borderRadius: "var(--radius-md)" }}>
              <div className="text-label" style={{ marginBottom: "var(--space-16)", color: "var(--ink-soft)" }}>Action Items</div>
              <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-12)" }}>
                {activeData.deliverables.map((item) => {
                  const isChecked = checkboxState[item.id] || false;
                  return (
                    <label key={item.id} style={{ display: "flex", gap: "var(--space-12)", alignItems: "flex-start", padding: "var(--space-12)", backgroundColor: "white", borderRadius: "var(--radius-sm)", border: isChecked ? "1px solid var(--success)" : "1px solid var(--line)", cursor: "pointer", transition: "all 0.2s", position: "relative" }}>
                      <div onClick={(e) => { e.preventDefault(); toggleCheckbox(item.id); }} style={{ minWidth: "20px", height: "20px", borderRadius: "4px", border: `2px solid ${isChecked ? "var(--success)" : "var(--line)"}`, backgroundColor: isChecked ? "var(--success)" : "white", color: "white", display: "flex", alignItems: "center", justifyContent: "center", marginTop: "2px" }}>{isChecked && <Icons.Check />}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: "15px", color: isChecked ? "var(--ink-lighter)" : "var(--ink)", textDecoration: isChecked ? "line-through" : "none", lineHeight: "1.4" }}>{item.text}</div>
                        <div style={{ fontSize: "12px", color: "var(--ink-lighter)", marginTop: "4px" }}>Owner: <span style={{ color: "var(--brand)" }}>{item.owner}</span></div>
                        {item.links && item.links.length > 0 && (
                          <div style={{ display: "flex", gap: "6px", marginTop: "8px", flexWrap: "wrap" }}>
                            {item.links.map((link, idx) => (
                              <button key={idx} onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (link.modalContent) { setInfoModalData({ content: link.modalContent, actionUrl: link.actionUrl, actionLabel: link.actionLabel }); } else { window.open(link.url, "_blank"); } }} style={{ display: "inline-flex", alignItems: "center", gap: "4px", padding: "2px 8px", borderRadius: "99px", backgroundColor: "var(--brand-light)", color: "var(--brand)", fontSize: "11px", fontWeight: 600, textDecoration: "none", border: "1px solid rgba(0, 86, 210, 0.1)", transition: "all 0.15s", cursor: "pointer" }} onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(0, 86, 210, 0.1)")} onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--brand-light)")}>
                                {link.label}
                                {link.modalContent ? <Icons.Info width={10} height={10} /> : <Icons.ExternalLink width={10} height={10} />}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
        </StandardModal>
      )}

      <InfoModal 
        isOpen={!!infoModalData} 
        onClose={() => setInfoModalData(null)} 
        content={infoModalData?.content} 
        actionUrl={infoModalData?.actionUrl} 
        actionLabel={infoModalData?.actionLabel} 
      />
    </AppShell>
  );
}
